/**
 * The firm's public booking page (/book/<firm>) and the page behind each confirmation's link
 * (/book/manage#token). Nobody signs in: a new visitor confirms their email with a code.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { CalendarCheck, CalendarPlus, CheckCircle2, Download, MailCheck } from "lucide-react";
import { useState } from "react";
import { LocationNote, money, SlotPicker, TextOptIn, whenLabel, googleCalendarUrl } from "@/components/booking-ui";
import { Empty, Loading } from "@/components/page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ApiError, errorMessage } from "@/lib/api";
import { cn } from "@/lib/utils";

type PublicType = { id: string; name: string; description: string; durationMinutes: number; location: "video" | "phone"; rule: string; priceCents: number; businessPriceCents: number };
type PublicPage = { firmName: string; timeZone: string; cancelHours: number; paymentsEnabled: boolean; texts: boolean; types: PublicType[] };

export async function call<T>(url: string, init: RequestInit = {}, token?: string): Promise<T> {
  const res = await fetch(url, { ...init, credentials: "omit", headers: { "x-clientflow-csrf": "1", ...(init.body ? { "Content-Type": "application/json" } : {}), ...(token ? { "x-link-token": token } : {}), ...(init.headers ?? {}) } });
  const body = res.headers.get("content-type")?.includes("application/json") ? await res.json() : {};
  if (!res.ok) throw new ApiError(res.status, body.error?.code ?? "error", body.error?.message ?? "The request could not be completed. Please try again.", body.error?.details);
  return body as T;
}

export function Frame({ firm, children, icon: Icon = CalendarCheck }: { firm?: string; children: React.ReactNode; icon?: typeof CalendarCheck }) {
  return (
    <div className="min-h-screen bg-muted/30">
      <header className="border-b border-border bg-card">
        <div className="mx-auto flex max-w-3xl items-center gap-2 px-4 py-4">
          <Icon className="h-5 w-5 text-primary" />
          <span className="font-semibold">{firm ?? "Book a meeting"}</span>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-4 py-8">{children}</main>
    </div>
  );
}

function priceText(t: PublicType, kind: "individual" | "business") {
  const p = kind === "business" ? t.businessPriceCents : t.priceCents;
  return p ? money(p) : "Free";
}

/** Where the visitor came from, when the link was tagged (?source=instagram, or utm_source / utm_campaign). */
export function linkTag(): { source?: string; campaign?: string } {
  const p = new URLSearchParams(window.location.search);
  const source = (p.get("source") || p.get("utm_source") || "").slice(0, 40);
  const campaign = (p.get("utm_campaign") || p.get("campaign") || "").slice(0, 200);
  return { ...(source ? { source } : {}), ...(campaign ? { campaign } : {}) };
}

export function PublicBookingPage({ slug }: { slug: string }) {
  const { data, isLoading, error } = useQuery<PublicPage>({ queryKey: ["public-book", slug], queryFn: () => call(`/api/public/book/${encodeURIComponent(slug)}`), retry: false });
  const [kind, setKind] = useState<"individual" | "business">("individual");
  const [typeId, setTypeId] = useState<string | null>(null);
  const [slot, setSlot] = useState<string | null>(null);
  const [v, setV] = useState({ name: "", email: "", phone: "", note: "", textMe: false });
  const [pending, setPending] = useState<{ bookingId: string; email: string } | null>(null);
  const [code, setCode] = useState("");
  const [done, setDone] = useState<string | null>(null);
  const type = data?.types.find((t) => t.id === typeId) ?? null;
  const start = useMutation({
    mutationFn: () => call<{ bookingId: string }>(`/api/public/book/${encodeURIComponent(slug)}`, { method: "POST", body: JSON.stringify({ meetingTypeId: typeId, startsAt: slot, kind, ...v, ...linkTag() }) }),
    onSuccess: (r) => setPending({ bookingId: r.bookingId, email: v.email }),
  });
  const verify = useMutation({
    mutationFn: () => call<{ status: string; checkoutUrl: string | null; manageToken: string }>(`/api/public/book/${encodeURIComponent(slug)}/verify`, { method: "POST", body: JSON.stringify({ bookingId: pending!.bookingId, code }) }),
    onSuccess: (r) => {
      if (r.checkoutUrl) window.location.assign(r.checkoutUrl);
      else setDone(r.manageToken);
    },
  });

  if (isLoading) return <Loading />;
  if (error || !data)
    return (
      <Frame>
        <Empty>{error instanceof ApiError && error.status === 404 ? "This booking page doesn't exist. Check the link with the firm." : errorMessage(error)}</Empty>
      </Frame>
    );
  if (done)
    return (
      <Frame firm={data.firmName}>
        <div className="rounded-xl border border-card-border bg-card p-6 text-center shadow-[var(--card-shadow)]">
          <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
          <h1 className="mt-3 text-xl font-semibold">You're booked</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {type?.name} on {slot && whenLabel(slot, data.timeZone)}. A confirmation is on its way to {pending?.email}.
          </p>
          <Button className="mt-4" variant="outline" onClick={() => window.location.assign(`/book/manage#${done}`)}>
            View or add to calendar
          </Button>
        </div>
      </Frame>
    );

  return (
    <Frame firm={data.firmName}>
      <h1 className="text-2xl font-semibold">Book a meeting</h1>
      <p className="mb-6 mt-1 text-sm text-muted-foreground">Choose a meeting and a time that suits you. Already a client? Sign in to your portal to book meetings included with your services.</p>

      <div className="mb-4 flex flex-wrap items-center gap-2 text-sm">
        <span className="text-muted-foreground">Booking for</span>
        {(["individual", "business"] as const).map((k) => (
          <button key={k} type="button" onClick={() => setKind(k)} className={cn("rounded-full border px-3 py-1 text-xs font-medium", kind === k ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card")} aria-pressed={kind === k}>
            {k === "individual" ? "Myself or my family" : "A business"}
          </button>
        ))}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {data.types.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => {
              setTypeId(t.id);
              setSlot(null);
            }}
            className={cn("rounded-xl border bg-card p-4 text-left shadow-[var(--card-shadow)] transition-colors", typeId === t.id ? "border-primary ring-1 ring-primary" : "border-card-border hover:border-primary/50")}
          >
            <span className="flex items-start justify-between gap-2">
              <span className="font-medium">{t.name}</span>
              <span className="shrink-0 text-sm font-semibold tabular">{priceText(t, kind)}</span>
            </span>
            <span className="mt-1 block text-sm text-muted-foreground">{t.description}</span>
            <span className="mt-2 block">
              <LocationNote location={t.location} durationMinutes={t.durationMinutes} />
            </span>
          </button>
        ))}
      </div>

      {type && (
        <section className="mt-6 rounded-xl border border-card-border bg-card p-5 shadow-[var(--card-shadow)]">
          <h2 className="mb-3 font-semibold">Choose a time</h2>
          <SlotPicker queryKey={`${slug}:${type.id}`} timeZone={data.timeZone} value={slot} onChange={setSlot} load={(from, to) => call<string[]>(`/api/public/book/${encodeURIComponent(slug)}/slots?meetingTypeId=${type.id}&from=${from}&to=${to}`)} />
        </section>
      )}

      {type && slot && !pending && (
        <form
          className="mt-6 space-y-3 rounded-xl border border-card-border bg-card p-5 shadow-[var(--card-shadow)]"
          onSubmit={(e) => {
            e.preventDefault();
            start.mutate();
          }}
        >
          <h2 className="font-semibold">Your details</h2>
          <p className="text-sm text-muted-foreground">
            {type.name} · {whenLabel(slot, data.timeZone)} · {priceText(type, kind)}
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="b-name">Name</Label>
              <Input id="b-name" required autoComplete="name" value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="b-email">Email</Label>
              <Input id="b-email" type="email" required autoComplete="email" value={v.email} onChange={(e) => setV({ ...v, email: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="b-phone">{v.textMe ? "Mobile phone" : `Phone${type.location === "phone" ? "" : " (optional)"}`}</Label>
              <Input id="b-phone" type="tel" autoComplete="tel" required={type.location === "phone" || v.textMe} value={v.phone} onChange={(e) => setV({ ...v, phone: e.target.value })} />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="b-note">What would you like to talk about? (optional)</Label>
            <Textarea id="b-note" rows={3} value={v.note} onChange={(e) => setV({ ...v, note: e.target.value })} />
          </div>
          {data.texts && <TextOptIn id="b-texts" firm={data.firmName} checked={v.textMe} onChange={(textMe) => setV({ ...v, textMe })} />}
          {start.error && <p className="text-sm text-destructive">{errorMessage(start.error)}</p>}
          <div className="flex flex-wrap items-center gap-3">
            <Button type="submit" disabled={start.isPending}>
              Continue
            </Button>
            <span className="text-xs text-muted-foreground">We'll email you a code to confirm. {(kind === "business" ? type.businessPriceCents : type.priceCents) > 0 ? (data.paymentsEnabled ? "Then you'll pay securely by card." : "We'll send you an invoice.") : ""}</span>
          </div>
        </form>
      )}

      {pending && (
        <form
          className="mt-6 space-y-3 rounded-xl border border-card-border bg-card p-5 shadow-[var(--card-shadow)]"
          onSubmit={(e) => {
            e.preventDefault();
            verify.mutate();
          }}
        >
          <h2 className="flex items-center gap-2 font-semibold">
            <MailCheck className="h-5 w-5 text-primary" /> Check your email
          </h2>
          <p className="text-sm text-muted-foreground">We sent a 6-digit code to {pending.email}. Your time is held for 15 minutes.</p>
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1">
              <Label htmlFor="b-code">Code</Label>
              <Input id="b-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} className="w-32 tabular" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} />
            </div>
            <Button type="submit" disabled={code.length !== 6 || verify.isPending}>
              Confirm booking
            </Button>
          </div>
          {verify.error && <p className="text-sm text-destructive">{errorMessage(verify.error)}</p>}
        </form>
      )}
    </Frame>
  );
}

type Managed = {
  firmName: string;
  slug: string;
  timeZone: string;
  cancelHours: number;
  booking: { status: string; startsAt: string; endsAt: string; typeName: string; location: "video" | "phone"; durationMinutes: number; meetingUrl: string; charge: string; priceCents: number; staffName: string; contactName: string; lateToCancel: boolean };
};

export function BookingManagePage() {
  const [token] = useState(() => window.location.hash.slice(1));
  const paid = new URLSearchParams(window.location.search).get("paid");
  const { data, isLoading, error, refetch } = useQuery<Managed>({ queryKey: ["manage-booking", token], queryFn: () => call("/api/public/book/manage/booking", {}, token), enabled: token.length >= 20, retry: false });
  const cancel = useMutation({ mutationFn: () => call("/api/public/book/manage/cancel", { method: "POST", body: "{}" }, token), onSuccess: () => refetch() });
  const [confirming, setConfirming] = useState(false);
  const download = async () => {
    const res = await fetch("/api/public/book/manage/ics", { credentials: "omit", headers: { "x-link-token": token } });
    const url = URL.createObjectURL(await res.blob());
    const a = Object.assign(document.createElement("a"), { href: url, download: "meeting.ics" });
    a.click();
    URL.revokeObjectURL(url);
  };
  if (token.length < 20)
    return (
      <Frame>
        <h1 className="sr-only">Manage your booking</h1>
        <Empty>This link is incomplete. Please open it directly from your confirmation email.</Empty>
      </Frame>
    );
  if (isLoading) return <Loading />;
  if (error || !data)
    return (
      <Frame>
        <h1 className="sr-only">Manage your booking</h1>
        <Empty>{errorMessage(error)}</Empty>
      </Frame>
    );
  const b = data.booking;
  const status =
    b.status === "cancelled" ? "Cancelled" : b.status === "pending_payment" ? (paid === "1" ? "Payment received; confirming…" : "Waiting for payment") : b.status === "confirmed" ? "Confirmed" : b.status === "completed" ? "Held" : b.status;
  return (
    <Frame firm={data.firmName}>
      <div className="rounded-xl border border-card-border bg-card p-6 shadow-[var(--card-shadow)]">
        <p className={cn("text-xs font-semibold uppercase tracking-wide", b.status === "cancelled" ? "text-destructive" : "text-primary")}>{status}</p>
        <h1 className="mt-1 text-xl font-semibold">{b.typeName}</h1>
        <p className="mt-1 text-sm">{whenLabel(b.startsAt, data.timeZone)}</p>
        <p className="mt-1 text-sm text-muted-foreground">
          With {b.staffName} · <LocationNote location={b.location} durationMinutes={b.durationMinutes} />
        </p>
        {b.meetingUrl && b.status === "confirmed" && (
          <p className="mt-3 text-sm">
            Video link:{" "}
            <a className="font-medium text-primary hover:underline" href={b.meetingUrl} target="_blank" rel="noreferrer">
              {b.meetingUrl}
            </a>
          </p>
        )}
        {b.status === "confirmed" && (
          <div className="mt-5 flex flex-wrap gap-2">
            <Button asChild variant="outline" size="sm">
              <a href={googleCalendarUrl(`${b.typeName} — ${data.firmName}`, b.startsAt, b.endsAt, b.meetingUrl ? `Join: ${b.meetingUrl}` : "")} target="_blank" rel="noreferrer">
                <CalendarPlus className="h-4 w-4" /> Google Calendar
              </a>
            </Button>
            <Button variant="outline" size="sm" onClick={() => void download()}>
              <Download className="h-4 w-4" /> Outlook or Apple (.ics)
            </Button>
          </div>
        )}
        {(b.status === "confirmed" || b.status === "pending_payment") && (
          <div className="mt-6 border-t border-border pt-4">
            {!confirming ? (
              <Button variant="ghost" size="sm" className="text-destructive" onClick={() => setConfirming(true)}>
                Cancel this meeting
              </Button>
            ) : (
              <div className="space-y-2 text-sm">
                <p>{b.lateToCancel && b.status === "confirmed" ? `It's less than ${data.cancelHours} hours away, so cancelling now still counts as a meeting${b.priceCents ? " and its charge stays" : ""}.` : "You can cancel without charge."}</p>
                <div className="flex gap-2">
                  <Button size="sm" variant="destructive" onClick={() => cancel.mutate()} disabled={cancel.isPending}>
                    Yes, cancel it
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setConfirming(false)}>
                    Keep it
                  </Button>
                </div>
                {cancel.error && <p className="text-destructive">{errorMessage(cancel.error)}</p>}
              </div>
            )}
          </div>
        )}
        {b.status === "cancelled" && (
          <Button className="mt-5" variant="outline" size="sm" onClick={() => window.location.assign(`/book/${data.slug}`)}>
            Book another time
          </Button>
        )}
      </div>
    </Frame>
  );
}
